import React, { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SitterCard from "@/components/SitterCard";
import PremiumGate from "@/components/PremiumGate";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, Filter, X } from "lucide-react";

const AVAILABILITIES = ["Weekdays", "Weekends", "Mornings", "Afternoons", "Evenings", "Overnight"];
const LANGUAGES = ["English", "Arabic", "French", "Urdu", "Spanish", "Turkish", "Somali", "Farsi", "Bengali"];

export default function Browse() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, is_premium: false, gated: false });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    postal_code: "", language: "", availability: "", min_rating: "",
    cpr: false, police: false, verified_only: false, q: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v === "" || v === false) return; params[k] = v; });
      const { data: r } = await api.get("/babysitters", { params });
      setData(r);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); load(); };
  const reset = () => {
    setFilters({ postal_code: "", language: "", availability: "", min_rating: "", cpr: false, police: false, verified_only: false, q: "" });
    setTimeout(load, 0);
  };

  const hidden = Math.max(0, data.total - data.items.length);
  const isFreeParent = user && user.role === "parent" && !user.is_premium;
  const isVisitor = !user;

  return (
    <div className="min-h-screen bg-linen">
      <Seo
        title="Browse verified caregivers"
        description="Search verified, CPR-certified caregivers by postal code, language, availability, and rating. Sakeenah Circle — a calmer way to find trusted childcare."
        path="/browse"
        jsonLd={breadcrumbSchema([{ name: "Find a caregiver", path: "/browse" }])}
      />
      <Nav />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-6">
        <Breadcrumb items={[{ name: "Find a caregiver" }]} />
        <span className="badge-sage mt-6 inline-block">Find your circle</span>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight text-forest">Browse verified caregivers</h1>
        <p className="text-forest/70 mt-2 max-w-2xl">Filter by postal code, language, availability and verification to find the perfect match.</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8">
        <form onSubmit={submit} className="card-organic p-5 md:p-6 grid gap-4 md:grid-cols-12" data-testid="browse-filters">
          <div className="md:col-span-3">
            <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Postal code</label>
            <input value={filters.postal_code} onChange={(e) => update("postal_code", e.target.value.toUpperCase())} placeholder="e.g. M5V" className="input-organic mt-1" data-testid="filter-postal" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Language</label>
            <select value={filters.language} onChange={(e) => update("language", e.target.value)} className="input-organic mt-1" data-testid="filter-language">
              <option value="">Any language</option>
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Availability</label>
            <select value={filters.availability} onChange={(e) => update("availability", e.target.value)} className="input-organic mt-1" data-testid="filter-availability">
              <option value="">Any time</option>
              {AVAILABILITIES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Min rating</label>
            <select value={filters.min_rating} onChange={(e) => update("min_rating", e.target.value)} className="input-organic mt-1" data-testid="filter-rating">
              <option value="">Any rating</option>
              <option value="4.5">4.5+</option>
              <option value="4.7">4.7+</option>
              <option value="4.9">4.9+</option>
            </select>
          </div>
          <div className="md:col-span-9 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-forest/80 cursor-pointer">
              <input type="checkbox" checked={filters.cpr} onChange={(e) => update("cpr", e.target.checked)} className="accent-forest w-4 h-4" data-testid="filter-cpr" /> CPR certified
            </label>
            <label className="flex items-center gap-2 text-sm text-forest/80 cursor-pointer">
              <input type="checkbox" checked={filters.police} onChange={(e) => update("police", e.target.checked)} className="accent-forest w-4 h-4" data-testid="filter-police" /> Police check
            </label>
            <label className="flex items-center gap-2 text-sm text-forest/80 cursor-pointer">
              <input type="checkbox" checked={filters.verified_only} onChange={(e) => update("verified_only", e.target.checked)} className="accent-forest w-4 h-4" data-testid="filter-verified" /> Verified only
            </label>
          </div>
          <div className="md:col-span-3 flex gap-2 md:justify-end">
            <button type="button" onClick={reset} className="btn-outline text-sm flex items-center gap-1.5" data-testid="filter-reset"><X className="w-4 h-4" /> Reset</button>
            <button type="submit" className="btn-primary text-sm flex items-center gap-1.5" data-testid="filter-search"><Search className="w-4 h-4" /> Search</button>
          </div>
        </form>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <p className="text-forest/70 text-sm" data-testid="results-count">
            {loading ? "Searching…" : `${data.items.length} of ${data.total} caregiver${data.total === 1 ? "" : "s"}`}
          </p>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="card-organic h-56 animate-pulse" />)}
          </div>
        ) : data.items.length === 0 ? (
          <div className="card-organic p-14 text-center">
            <Filter className="w-8 h-8 mx-auto text-forest/50" />
            <p className="font-serif text-2xl mt-3 text-forest">No caregivers match your filters</p>
            <p className="text-forest/60 mt-1 text-sm">Try relaxing your criteria — we'll help you find someone lovely.</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.items.map((s) => <SitterCard key={s.id} s={s} />)}
            </div>
            {(isFreeParent || isVisitor) && hidden > 0 && (
              <div className="mt-8" data-testid="browse-gate">
                <PremiumGate
                  title={`${hidden} more caregivers await`}
                  body="Free accounts see a curated sample. Upgrade to Premium for unlimited search, direct messaging and AI matching."
                  cta={isVisitor ? "Get started free" : "Upgrade to Premium — $9.99/mo"}
                />
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
